import React, {useState, useEffect} from "react";
import {Drawer, DatePicker, Tooltip, message} from "antd";
import {InfoCircleOutlined} from "@ant-design/icons";
import moment from "moment";
import locale from "antd/es/date-picker/locale/tr_TR";
import {useStore} from "hooks/useStore";
import {useFetch} from "hooks/useFetch";
import {CustomButton} from "components/_defaultComponents/CustomButton/CustomButton";
import {ResponsiveDataTable} from "components/_defaultComponents/ResponsiveDataTable/ResponsiveDataTable";

// Tutarı sabit boyutlu (padding 12, radius 8) renkli bir kutu içinde gösterir
const amountBox = (value, backgroundColor) => (
    <span
        style={{
            display: "inline-block",
            boxSizing: "border-box",
            width: 72,
            textAlign: "center",
            padding: "2px 4px",
            borderRadius: 6,
            backgroundColor,
            fontWeight: "bold"
        }}
    >
        {value} ₺
    </span>
);

// Eski fiyat: değiştiyse çok açık sarı kutu, değişmediyse şeffaf kutu (aynı hizada dursun)
const renderOldAmount = (oldValue, newValue) => {
    if (Number(oldValue) !== Number(newValue)) {
        return amountBox(oldValue, "#FFFDE7");
    }
    return amountBox(oldValue, "transparent");
};

// Yeni fiyat: arttıysa yeşil, azaldıysa kırmızı, değişmediyse şeffaf kutu (aynı hizada dursun)
const renderChangedAmount = (newValue, oldValue) => {
    const newNumber = Number(newValue);
    const oldNumber = Number(oldValue);

    if (newNumber > oldNumber) {
        return amountBox(newValue, "#C9F5CE");
    }
    if (newNumber < oldNumber) {
        return amountBox(newValue, "#FFCFC9");
    }
    return amountBox(newValue, "transparent");
};

const columns = [
    {title: "Kurye", dataIndex: "fullname", onCell: () => ({"data-label": "Kurye"})},
    {
        title: "Tarih",
        dataIndex: "date",
        onCell: () => ({"data-label": "Tarih"}),
        render: value => {
            if (!value) return "-";
            const parsed = moment(value, ["DD-MM-YYYY", "YYYY-MM-DD", moment.ISO_8601], true);
            return parsed.isValid() ? parsed.format("DD.MM.YYYY") : value;
        }
    },
    {title: "Firma Adı", dataIndex: "companyName", onCell: () => ({"data-label": "Firma Adı"})},
    {
        title: "Alt Firma Adı",
        dataIndex: "subCompanyName",
        onCell: () => ({"data-label": "Alt Firma Adı"})
    },
    {
        title: "Çalışma Süresi",
        dataIndex: "workTimeMinute",
        onCell: () => ({"data-label": "Çalışma Süresi"})
    },
    {
        title: "Paket Sayısı",
        dataIndex: "packageCount",
        onCell: () => ({"data-label": "Paket Sayısı"})
    },
    {
        title: "Eski Kurye Fiyatı",
        dataIndex: "oldCourierAmount",
        onCell: () => ({"data-label": "Eski Kurye Fiyatı"}),
        render: (value, record) => renderOldAmount(value, record?.newCourierAmount)
    },
    {
        title: "Yeni Kurye Fiyatı",
        dataIndex: "newCourierAmount",
        onCell: () => ({"data-label": "Yeni Kurye Fiyatı"}),
        render: (value, record) => renderChangedAmount(value, record?.oldCourierAmount)
    },
    {
        title: "Eski Müşteri Fiyatı",
        dataIndex: "oldClientAmount",
        onCell: () => ({"data-label": "Eski Müşteri Fiyatı"}),
        render: (value, record) => renderOldAmount(value, record?.newClientAmount)
    },
    {
        title: "Yeni Müşteri Fiyatı",
        dataIndex: "newClientAmount",
        onCell: () => ({"data-label": "Yeni Müşteri Fiyatı"}),
        render: (value, record) => renderChangedAmount(value, record?.oldClientAmount)
    }
];

export const RecalculatePastShiftsModal = ({visible, onCancel, contractId, onSuccess}) => {
    const stores = useStore();
    const [forecastDate, setForecastDate] = useState(null);
    const [isConfirming, setIsConfirming] = useState(false);

    const [loading, rows] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.recalculateSubCompanyPaymentContract.bind(null, {
            contractId,
            isForShowOnly: true
        }),
        dependencies: [visible, contractId],
        condition: !!visible && !!contractId,
        initialCall: !!visible && !!contractId,
        transform: result => result?.data?.rows || []
    });

    // Modal her açıldığında yansıtma tarihi seçimini sıfırla
    useEffect(() => {
        if (visible) {
            setForecastDate(null);
        }
    }, [visible]);

    const handleConfirm = () => {
        if (!forecastDate) {
            message.warning("Vardiyaların yansıtılacağı tarihi seçiniz");
            return;
        }

        stores.appStore.openConfirmModal({
            content: `Tüm kurye kayıtlarındaki değişiklikler ${forecastDate.format(
                "DD.MM.YYYY"
            )} tarihine yansıtılacaktır, onaylıyor musunuz?`,
            onOk: async () => {
                setIsConfirming(true);
                try {
                    const result = await stores.companyStore.recalculateSubCompanyPaymentContract({
                        contractId,
                        forecastDate: forecastDate.format("YYYY-MM-DD"),
                        isForShowOnly: false
                    });

                    if (result?.success) {
                        message.success(
                            result?.message || "Geçmiş vardiyalar başarıyla yeniden hesaplandı"
                        );
                        onSuccess?.();
                        onCancel();
                    } else {
                        message.error(result?.message || "İşlem gerçekleştirilemedi");
                    }
                } catch (error) {
                    message.error(error.message || "Bir hata oluştu");
                } finally {
                    setIsConfirming(false);
                }
            }
        });
    };

    return (
        <Drawer
            title={<b>Tekrar Hesaplanan Vardiyalar</b>}
            placement="right"
            width={"100%"}
            closable={true}
            onClose={onCancel}
            visible={visible}
            bodyStyle={{display: "flex", flexDirection: "column"}}
        >
            <div style={{flex: 1, overflow: "auto"}}>
                <div style={{marginBottom: 20}}>
                    <div style={{marginBottom: 6, fontWeight: 600}}>
                        Varsa Ücret Değişimlerinin Yansıtılacağı Tarih{" "}
                        <Tooltip title="Sözleşme değiştirildiğinde kuryenin varsa ücret değişimlerinin hangi güne yansıtılacağı tarihin seçildiği alandır.">
                            <InfoCircleOutlined style={{color: "#1890ff"}} />
                        </Tooltip>
                    </div>
                    <DatePicker
                        locale={locale}
                        value={forecastDate}
                        format="DD.MM.YYYY"
                        onChange={date => setForecastDate(date)}
                        disabledDate={current =>
                            current &&
                            (current > moment().endOf("day") ||
                                current <
                                    moment()
                                        .subtract(15, "days")
                                        .startOf("day"))
                        }
                        style={{width: 300, maxWidth: "100%"}}
                    />
                </div>
                <ResponsiveDataTable
                    columns={columns}
                    dataSource={rows}
                    loading={loading}
                    rowKey={(record, index) => record?._id || index}
                    size="small"
                    pagination={{pageSize: 10, hideOnSinglePage: true}}
                    scroll={{x: "max-content"}}
                    onRow={record => {
                        const hasChange =
                            Number(record?.newCourierAmount) !== Number(record?.oldCourierAmount) ||
                            Number(record?.newClientAmount) !== Number(record?.oldClientAmount);
                        return hasChange;
                    }}
                />
            </div>
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                    paddingTop: 16,
                    borderTop: "1px solid #f0f0f0"
                }}
            >
                <CustomButton onClick={onCancel}>İptal</CustomButton>
                <CustomButton type="primary" onClick={handleConfirm} loading={isConfirming}>
                    Değişiklikleri Onayla
                </CustomButton>
            </div>
        </Drawer>
    );
};
